import React, { useEffect, useState } from "react";
import LogoCEA from "../assets/images/logo.png";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import SyncLoader from "react-spinners/SyncLoader";

const formatRupiah = (n) =>
  n === null || n === undefined ? "" : `Rp${Number(n).toLocaleString("id-ID")}`;

// Menyamarkan email di layar konfirmasi (mis. "bu***@gmail.com"). Halaman ini
// publik dan hanya dijaga oleh order-id, jadi jangan menampilkan alamat email
// lengkap milik orang lain kepada siapa pun yang kebetulan memegang link-nya.
const maskEmail = (email) => {
  if (!email || !email.includes("@")) return "your registered email";
  const [name, domain] = email.split("@");
  const head = name.slice(0, 2);
  return `${head}${"*".repeat(Math.max(name.length - 2, 1))}@${domain}`;
};

const ResendPayment = () => {
  const navigate = useNavigate();
  const { orderId } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [sentTo, setSentTo] = useState("");

  // Path backend sengaja "/api/resend-payment" (beda dari path halaman ini
  // sendiri, "/resend-payment") — lihat komentar di ResendPaymentRoute.js.
  useEffect(() => {
    axios
      .get(`/api/resend-payment/${orderId}`)
      .then((res) => setSummary(res.data))
      .catch((err) =>
        setErrorMsg(err.response?.data?.msg || "This link is no longer valid."),
      )
      .finally(() => setIsLoading(false));
  }, [orderId]);

  const handleResend = async () => {
    setSending(true);
    try {
      const res = await axios.post(`/api/resend-payment/${orderId}`);
      setSentTo(maskEmail(res.data?.email));
    } catch (err) {
      Swal.fire({
        text: err.response?.data?.msg || "Failed to send a new payment link.",
        icon: "error",
        background: "white",
        color: "black",
        confirmButtonColor: "#fb3104",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: "#f4f4f4",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: "40px 16px",
      }}
    >
      <div
        style={{
          backgroundColor: "#fff",
          borderRadius: "12px",
          padding: "40px",
          width: "100%",
          maxWidth: "480px",
          boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
          textAlign: "center",
        }}
      >
        <img
          src={LogoCEA}
          alt="CEA Logo"
          style={{ width: "64px", height: "64px", objectFit: "contain", marginBottom: "12px" }}
        />
        <h4 style={{ fontWeight: "700", color: "#fb3104", marginBottom: "20px" }}>
          Request a New Payment Link
        </h4>

        {isLoading ? (
          <div style={{ padding: "20px 0" }}>
            <SyncLoader color="#fb3104" size={10} />
          </div>
        ) : sentTo ? (
          <>
            <p style={{ color: "#28a745", fontWeight: "600", fontSize: "15px", marginBottom: "8px" }}>
              A new payment link has been sent to {sentTo}.
            </p>
            <p style={{ fontSize: "13px", color: "#999", marginBottom: "20px", lineHeight: 1.6 }}>
              Please check your inbox (and your spam folder). The new link is valid for 3 days —
              this page's old link no longer works.
            </p>
            <button
              type="button"
              onClick={() => navigate("/")}
              style={{
                marginTop: "4px",
                padding: "10px 24px",
                backgroundColor: "#fb3104",
                color: "#fff",
                border: "none",
                borderRadius: "8px",
                fontWeight: "700",
                fontSize: "14px",
                cursor: "pointer",
              }}
            >
              Back to Homepage
            </button>
          </>
        ) : errorMsg || !summary ? (
          <>
            <p style={{ color: "#888", fontSize: "14px", lineHeight: 1.6 }}>{errorMsg}</p>
            <a
              href="https://wa.me/628984001333"
              target="_blank"
              rel="noreferrer"
              style={{
                display: "inline-block",
                marginTop: "16px",
                padding: "10px 24px",
                backgroundColor: "#fb3104",
                color: "#fff",
                textDecoration: "none",
                borderRadius: "8px",
                fontWeight: "700",
                fontSize: "14px",
              }}
            >
              Contact Admin on WhatsApp
            </a>
            <button
              type="button"
              onClick={() => navigate("/")}
              style={{
                display: "block",
                width: "100%",
                marginTop: "12px",
                padding: "10px",
                backgroundColor: "transparent",
                color: "#888",
                border: "1px solid #ddd",
                borderRadius: "8px",
                fontWeight: "600",
                fontSize: "14px",
                cursor: "pointer",
              }}
            >
              Back to Homepage
            </button>
          </>
        ) : (
          <>
            <p style={{ color: "#444", fontSize: "15px", marginBottom: "6px" }}>
              We'll send a fresh payment link for:
            </p>
            <p style={{ fontWeight: "700", fontSize: "17px", margin: "0 0 4px" }}>{summary.title}</p>
            <p style={{ color: "#666", fontSize: "14px", marginBottom: summary.amount ? "6px" : "24px" }}>
              {summary.subtitle}
            </p>
            {summary.amount ? (
              <p style={{ color: "#fb3104", fontWeight: "700", fontSize: "18px", marginBottom: "24px" }}>
                {formatRupiah(summary.amount)}
              </p>
            ) : null}
            <p style={{ fontSize: "13px", color: "#999", marginBottom: "24px", lineHeight: 1.6 }}>
              The link will be emailed to {maskEmail(summary.email)} — the address used when this
              registration was made. Your previous link will stop working.
            </p>
            <button
              type="button"
              onClick={handleResend}
              disabled={sending}
              style={{
                width: "100%",
                padding: "10px",
                backgroundColor: "#fb3104",
                color: "#fff",
                border: "none",
                borderRadius: "8px",
                fontWeight: "700",
                fontSize: "15px",
                cursor: sending ? "not-allowed" : "pointer",
                opacity: sending ? 0.7 : 1,
              }}
            >
              {sending ? "Sending..." : "Send Me a New Payment Link"}
            </button>
            <button
              type="button"
              onClick={() => navigate("/")}
              disabled={sending}
              style={{
                width: "100%",
                marginTop: "10px",
                padding: "10px",
                backgroundColor: "transparent",
                color: "#888",
                border: "1px solid #ddd",
                borderRadius: "8px",
                fontWeight: "600",
                fontSize: "14px",
                cursor: sending ? "not-allowed" : "pointer",
              }}
            >
              Back to Homepage
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default ResendPayment;
